<template>
  <div>
    <div class="vx-row placeholder-opacity-100 placeholder-black::placeholder">
      <div
        :class="`vx-col w-full md:w-1/${columns === undefined ? 3 : columns}`"
        v-for="(mod, index) in value.template.filter(field => field.input !== 'localized' && field.input !== 'repeater' && showInput(field))"
        :key="mod.key"
      >
          <div>
              <label-form
                  :margins="index < columns ? 'mb-1': 'mt-3 mb-1'"
                  font-size="text-sm"
                  :info="mod.subtitle ? itemName(mod.subtitle) : null"
              >
                  {{ itemName(mod.title, mod.required) }}
              </label-form>

              <DynamicFormField
                  :field="mod"
                  :value="value.values ? value.values[mod.key] : null"
                  :is-read-only="isReadOnly"
                  :condensed-selects="condensedSelects"
                  @input="update($event, mod.key)"
                  @refresh="$emit('refresh')"
              />
          </div>
      </div>

      <div
          class="vx-col w-full"
          v-if="localizedFields.template.length > 0"
      >
          <LocalizedContent
              :fields-definition="localizedFields.template"
              :languages="languages"
              :fixed-languages="true"
              :value="localizedFields.values"
              @input="handleLocalizedInput"
              :columns="columns || 2"
          />
      </div>

      <div
          class="vx-col w-full mt-3"
          v-for="repeaterField in repeaterFieldDefs"
          :key="repeaterField.key"
      >
          <label-form margins="mb-1" font-size="text-sm" :info="repeaterField.subtitle ? itemName(repeaterField.subtitle) : null">
              {{ itemName(repeaterField.title, repeaterField.required) }}
          </label-form>

          <draggable
              :list="getRepeaterValue(repeaterField.key)"
              handle=".drag-handle"
              :animation="150"
              :disabled="isReadOnly || !repeaterField.sortable"
              @end="onRepeaterSort(repeaterField.key, $event)"
          >
              <div
                  v-for="(row, rowIndex) in getRepeaterValue(repeaterField.key)"
                  :key="rowIndex"
                  class="mt-2 border border-grey-light rounded"
              >
                  <div v-if="!isReadOnly" class="flex justify-end px-3 pt-2">
                      <button v-if="repeaterField.sortable" class="drag-handle cursor-move text-grey hover:text-dark border-0 bg-transparent p-0 mr-auto">
                          <feather-icon icon="MenuIcon" svgClasses="w-4 h-4" />
                      </button>
                      <button
                          class="sk-btn sk-btn-small btn-border sk-btn-danger rounded-lg"
                          @click.stop="removeRepeaterRow(repeaterField.key, rowIndex)"
                      >
                          <feather-icon icon="TrashIcon" svgClasses="text-danger w-4" />
                      </button>
                  </div>
                  <div class="vx-row px-3 pb-3">
                      <div
                          v-for="(subField, subIndex) in repeaterField.template"
                          :key="subField.key"
                          :class="`vx-col w-full md:w-1/${columns === undefined ? 3 : columns}`"
                      >
                          <label-form
                              :margins="subIndex < (columns === undefined ? 3 : columns) ? 'mb-1' : 'mt-3 mb-1'"
                              font-size="text-sm"
                          >{{ itemName(subField.title) }}</label-form>

                          <DynamicFormField
                              :field="subField"
                              :value="row[subField.key]"
                              :is-read-only="isReadOnly"
                              :condensed-selects="condensedSelects"
                              @input="updateRepeaterRow(repeaterField.key, rowIndex, subField.key, $event)"
                          />
                      </div>
                  </div>
              </div>
          </draggable>

          <button
              v-if="!isReadOnly"
              class="mt-2 sk-btn sk-btn-primary rounded-lg"
              @click.stop="addRepeaterRow(repeaterField.key, repeaterField.template)"
          >
              {{ $t('shared.add') }}
          </button>
      </div>
    </div>
  </div>
</template>

<script>
import LabelForm from "@/modules/Shared/Components/form/LabelForm.vue";
import LocalizedContent from "@/modules/Shared/Components/form/LocalizedContent.vue";
import DynamicFormField from "@/modules/Shared/Components/form/DynamicFormField.vue";
import draggable from 'vuedraggable'

export default {
  components: {
    DynamicFormField,
    LocalizedContent,
    LabelForm,
    draggable,
  },
  props: {
    "value": Object,
    "columns": Number,
    languages: {
        type: Array,
        default: () => {
            return ['en', 'es', 'fr', 'ca']
        }
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    condensedSelects: {
        type: Boolean,
        default: false
    }
  },
  computed: {
      repeaterFieldDefs() {
          return this.value.template.filter(field => field.input === 'repeater' && this.showInput(field))
      },
      localizedFields() {
          const fields = this.value.template
              .filter(field => field.input === 'localized')

          const values = this.value.values || {}

          return {
              template: fields.map(field => ({
                  name: field.key,
                  label: this.$t(field.title),
                  type: 'text',
              })),
              values: this.languages.map(lang => {
                  const objectLang =  {language: lang}

                  fields
                      .map(field => field.key)
                      .forEach(key => {
                        objectLang[key] = values[key] ? values[key][lang] : ''
                      })

                  return objectLang
              })
          }
      }
  },
  methods: {
    showInput(item) {
        return !item.hasOwnProperty('show') || item.show !== false
    },
    handleLocalizedInput(value) {
        this.value.values = this.value.values || {}
        value.forEach(localizedObject => {
            Object.keys(localizedObject)
                .filter(key => key !== 'language')
                .forEach(key => {
                    this.value.values[key] = this.value.values[key] || {}
                    this.value.values[key][localizedObject.language] = localizedObject[key]
                })
        })
    },
    getUserLanguage() {
      return this.$store.state.AppActiveUser.language
    },
    update(value, key) {
      this.$emit("input", {
        template: this.value.template,
        values: { ...this.value.values, [key]: value },
      });
      this.checkAllErrors()
    },
    hasValue(key) {
      if (!this.value.values) {
        return false
      }

      return !(this.value.values[key] === null || this.value.values[key] === undefined || this.value.values[key] === "");
    },
    itemName(name, required = false)
    {
      if (typeof name === 'string') {
        return this.$t(name) + (required ? ' *' : '')
      }

      return name[this.getUserLanguage()] + (required ? ' *' : '');
    },
    getRepeaterValue(key) {
      if (!this.value.values || !Array.isArray(this.value.values[key])) {
        return []
      }
      return this.value.values[key]
    },
    addRepeaterRow(key, template) {
      const emptyRow = {}
      template.forEach(field => { emptyRow[field.key] = '' })
      const updatedForm = {
        template: this.value.template,
        values: { ...this.value.values, [key]: [...this.getRepeaterValue(key), emptyRow] }
      }
      this.$emit('input', updatedForm)
      this.checkAllErrors()
    },
    onRepeaterSort(key, event) {
      const reordered = [...this.getRepeaterValue(key)]
      const [moved] = reordered.splice(event.oldIndex, 1)
      reordered.splice(event.newIndex, 0, moved)
      this.$emit('input', {
        template: this.value.template,
        values: { ...this.value.values, [key]: reordered }
      })
      this.checkAllErrors()
    },
    removeRepeaterRow(key, rowIndex) {
      const updated = this.getRepeaterValue(key).filter((_, i) => i !== rowIndex)
      const updatedForm = {
        template: this.value.template,
        values: { ...this.value.values, [key]: updated }
      }
      this.$emit('input', updatedForm)
      this.checkAllErrors()
    },
    updateRepeaterRow(key, rowIndex, fieldKey, value) {
      const updated = this.getRepeaterValue(key).map((row, i) =>
        i === rowIndex ? { ...row, [fieldKey]: value } : row
      )
      const updatedForm = {
        template: this.value.template,
        values: { ...this.value.values, [key]: updated }
      }
      this.$emit('input', updatedForm)
      this.checkAllErrors()
    },
    checkAllErrors() {
      let errors = 0;
      this.value.template.forEach(element => {
        if(element.required) {
          if(element.input === 'color' && !(/^#[A-Fa-f0-9]{8}$/.test(this.value.values[element.key]))) {
            errors++;
          }
          else if(element.input === 'repeater') {
            if(!Array.isArray(this.value.values[element.key]) || this.value.values[element.key].length === 0) {
              errors++;
            }
          }
          else {
            if(!this.hasValue(element.key)) {errors++;}
          }
        }
      });
      if(errors === 0) this.$emit("check-validation",true);
      else this.$emit("check-validation",false);
    }
  },
  updated() {
    this.checkAllErrors();
  },
  created() {
    this.checkAllErrors();
  }
};
</script>
